// ThemeContext.tsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  type ReactNode,
  useMemo,
} from "react";

const THEME_STORAGE_KEY = "cg-theme";

type ThemeContextType = {
  theme: string;
  changeTheme: (value: string) => void;
  toggleTheme: () => void;
  isDark: boolean;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<string>(() => {
    // Read from localStorage, fall back to "light"
    const val = localStorage.getItem(THEME_STORAGE_KEY) || "light";
    document.documentElement.setAttribute("data-theme", val);
    return val;
  });

  const changeTheme = (value: string) => {
    setTheme(value);
    localStorage.setItem(THEME_STORAGE_KEY, value);
    document.documentElement.setAttribute("data-theme", value);
  };

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    changeTheme(next);
  };

  const isDark = theme === "dark";

  const contextValue = useMemo(
    () => ({ theme, changeTheme, toggleTheme, isDark }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme]
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
